import { ChevronDown } from "lucide-react";
import React, { HTMLAttributes, PropsWithChildren } from "react";
import { Button, ResponsiveImage, RichText, SectionBadge } from "../ui";
import type { Section } from "../../content/schema-types";

type HeroContentProps = Extract<Section, { type: "hero" }>["content"];
export type HeroSectionProps = Omit<HeroContentProps, "badge"> &
    PropsWithChildren &
    HTMLAttributes<HTMLDivElement> & {
        badge?: string | React.ReactNode;
        badgeClassName?: string; // Allow customization of badge styling
        /**
         * Hebt die ZWEITE Titelzeile hervor statt sie zu verkleinern: gleiche
         * Groesse, Markenfarbe — das Muster von workos.com («Your app,»
         * schwarz, «Enterprise Ready.» farbig).
         *
         * ⚠️ Standard `false`. Die Verkleinerung auf `text-[0.8em]` ist kein
         * Versehen, sondern loest ein gemessenes Umbruchproblem (3.8.2026:
         * Claim brach auf vier von sechs Breiten). Dieselbe Komponente bedient
         * ALLE Layouts — ein frueherer `whitespace-nowrap` stellte
         * `/plattform`, `/angebote` und `/referenzen` senkrecht, ueber 8952px
         * hoch. Wer das Prop setzt, prueft die Seite auf schmalen Breiten nach.
         */
        highlightSecondLine?: boolean;
        /**
         * Vergroessert die ERSTE Titelzeile (Jan, 2.9.2026). Die zweite bleibt
         * auf Normalmass — beide gross nimmt der Titel den halben Bildschirm.
         *
         * Nur fuer kurze erste Zeilen: In der halben Spalte eines
         * Zwei-Spalten-Heros bricht ein langer Claim sonst um. Siehe die
         * Umbruch-Messung vom 3.8.2026 weiter unten.
         */
        oversizedTitle?: boolean;
        /**
         * Eine Komponente ANSTELLE des Bildes (12.9.2026).
         *
         * `image` nimmt einen Pfad und geht durch `ResponsiveImage` — richtig
         * fuer Dateien, unbrauchbar fuer etwas Gerendertes wie das wandernde
         * Bausteine-Band. Warum nicht `children`: Die tragen bereits den
         * absolut positionierten Mat-Teaser, der ueber dem Bild schwebt und
         * NEBEN dem Visual weiterbestehen muss.
         *
         * Gesetzt schlaegt `visual` das `image` — beide gleichzeitig zu zeigen
         * waere in einer Spalte ohnehin nicht darstellbar.
         */
        visual?: React.ReactNode;
        /**
         * Breite der Textspalte im Layout `single-column`, Standard
         * `max-w-4xl`. Eine Seite mit absolut positionierter Dekoration rechts
         * (KI-Technologie: der winkende Mat) schmaelert die Spalte damit nur
         * auf den Breiten, auf denen die Dekoration sonst den Text verdeckt.
         */
        singleColumnClassName?: string;
    };

export const HeroSection: React.FC<HeroSectionProps> = ({
    layout = "two-column",
    background = "gradient",
    minHeight = "min-h-[600px]",
    className = "pt-48 pb-20",
    badge,
    badgeVariant,
    title,
    subtitle,
    description,
    trustBadges,
    ctaButtons = [],
    image,
    imageAlt = "",
    imageClassName = "",
    discoverMore,
    withTextOverlay = false,
    highlightSecondLine = false,
    oversizedTitle = false,
    visual,
    singleColumnClassName = "max-w-4xl",
    badgeClassName = "",
    children,
}) => {
    /*
        Traegt dieser Hero ueberhaupt ein Bild?

        Nicht jedes Hero-Bild kommt als `image`-Prop. Die beiden KI-Workshops
        (ServiceAiPotentialAnalysis, ServiceAiAgentsWorkshop) reichen ihres als
        `children` herein — ein absolut positioniertes Dekorbild, rechts unten
        im Hero verankert, das hinter dem Text schweben soll statt in einer
        eigenen Spalte zu stehen.

        Die Zentrierungs-Regel unten fragte anfangs nur `image` ab. Fuer diese
        beiden Seiten sah der Hero damit bildlos aus, der Text wurde zentriert —
        und landete mitten auf dem Dekorbild (gemeldet 5.8.2026,
        /angebote/ki-potenzialanalyse).
    */
    const hasImage = Boolean(image) || Boolean(visual) || Boolean(children);

    // Background classes
    const bgClass = {
        gradient: "bg-gradient-to-b from-gradient-start to-gradient-end text-white",
        white: "bg-white text-gray-900",
        gray: "bg-gray-50 text-gray-900",
    }[background];

    /*
        Die Badge-Farbe folgt dem HINTERGRUND, nicht einer festen Vorgabe
        (Jan, 12.8.2026).

        Vorher stand hier `badgeVariant = "light"` — violetter Text
        (`tertiary-light`) auf violettem Verlauf, und kein Aufrufer setzte etwas
        anderes. Der Badge war auf allen 31 Hero-Sektionen kaum lesbar; am
        deutlichsten fiel es am Track-Badge auf, weil der die Zugehoerigkeit
        tragen soll und dafuer gelesen werden muss.

        `gradient` ist dunkel, `white` und `gray` sind hell — die Variante daraus
        abzuleiten ist richtiger als eine Vorgabe, die beim naechsten hellen Hero
        wieder falsch waere. Ein ausdruecklich gesetzter `badgeVariant` schlaegt
        die Ableitung.
    */
    const effektiveBadgeVariant = badgeVariant ?? (background === "gradient" ? "secondary" : "light");

    // Render description (handle array or string, always use RichText)
    const renderDescription = () => {
        if (!description) {
            return null;
        }
        const content = Array.isArray(description) ? description.join(" ") : description;

        return (
            /* `text-balance`: Lead-Text, kein Fliesstext — siehe
               `docs/rules/coding-style.md` → «Zeilenumbruch». */
            <div className="text-balance text-lg md:text-xl leading-relaxed mb-2 md:mb-6">
                <RichText content={content} />
            </div>
        );
    };

    // Text shadow for overlay style
    const textShadowStyle = withTextOverlay ? { textShadow: "0 4px 20px rgba(0,0,0,0.5)" } : {};
    const subtitleShadowStyle = withTextOverlay ? { textShadow: "0 2px 10px rgba(0,0,0,0.5)" } : {};

    // Helper function to render title with line breaks
    const renderTitle = () => {
        if (!title) {
            return null;
        }

        // Split by \n and render with <br /> tags
        // Leere Zeilen weg: der YAML-Block `title: |` behaelt den
        // abschliessenden Umbruch, daraus entstand eine dritte, leere Zeile
        // samt `<br>` — sichtbar als Leerraum unter dem Claim.
        const parts = title.split("\n").filter(part => part.trim().length > 0);
        if (parts.length === 1) {
            return title;
        }

        return (
            <>
                {parts.map((part, index) => (
                    /*
                        Jede Content-Zeile ein eigener Block (Jan, 3.8.2026).
                        Vorher teilten sich alle Zeilen eine Schriftgroesse und
                        eine Umbruchregel — bei «Quatico.» (192px) und «Das
                        Engineering-Haus.» (508px) kann dieselbe Regel nicht
                        fuer beide stimmen. Der Claim brach dadurch auf vier von
                        sechs gemessenen Breiten (2 Zeilen bei 1440px, 3 bei
                        1024px).

                        Ab der zweiten Zeile deshalb eine Stufe kleiner: der
                        Claim bekommt Platz, ohne dass die Marke schrumpft.
                        Beides bleibt im `h1` — eine Ueberschrift, zwei
                        Stilzonen (Entscheid Jan).

                        KEIN `whitespace-nowrap`: das war der erste Versuch und
                        zerlegte alle einspaltigen Heros (`/plattform`,
                        `/angebote`, `/referenzen` …) — der Titel stand dort
                        senkrecht, ein Buchstabe je Zeile, ueber 8952px hoch.
                        Dieselbe Komponente bedient alle Layouts; was in der
                        halben Spalte der Startseite passt, muss anderswo
                        brechen duerfen. `text-wrap: balance` verteilt
                        gleichmaessig, erzwingt aber nichts.
                    */
                    /* `hyphens-none break-normal` (12.9.2026): `index.css` trennt
                       Ueberschriften absichtlich mit `hyphens: auto`, damit lange
                       Komposita nicht mitten im Wort brechen — richtig fuer
                       Ueberschriften ueber die volle Breite. Der Hero-Titel steht
                       aber in einer Zweispalten-Anordnung und hat nur die halbe
                       Seite: 432px bei 1440, 325px bei 1024. Dort liefert die Regel
                       genau das Ergebnis, das sie verhindern sollte
                       («Fachan-wendung», «Anwen-dung», und die heutige Startseite
                       trennt «Engineering-Haus.» seit jeher). Im Browser gemessen:
                       8 Zeilen Titel bei 1024px.

                       Die globale Regel bleibt unangetastet — sie hilft ueberall
                       dort, wo eine Ueberschrift die volle Breite hat. Nur der
                       Hero nimmt sie zurueck. */
                    <span
                        key={index}
                        className={`block text-balance hyphens-none break-normal ${
                            index > 0
                                ? highlightSecondLine
                                    ? "text-secondary"
                                    : "text-[0.8em]"
                                : oversizedTitle
                                  ? "text-[1.6em]"
                                  : ""
                        }`}
                    >
                        {part}
                    </span>
                ))}
            </>
        );
    };

    // Render content section (reusable across all layouts)
    const renderContent = () => (
        <div className="relative">
            {/* Text overlay for SolutionHero pattern */}
            {withTextOverlay && <div className="absolute -inset-8 -z-10 bg-primary/95 rounded-3xl blur-2xl" />}

            {/* Badge (string badges only, ReactNode badges render on image) */}
            {badge && typeof badge === "string" && (
                <div className="mb-4">
                    <SectionBadge variant={effektiveBadgeVariant} className={badgeClassName}>
                        {badge}
                    </SectionBadge>
                </div>
            )}

            {/* Title */}
            {/*
                Schriftgroesse an der Spaltenbreite statt an festen Stufen
                (Jan, 3.8.2026).

                Die Zeilen sind umbruchfest (`whitespace-nowrap`) — dann MUSS
                die Schrift mitgehen, sonst steht der Text ueber statt
                umzubrechen. Gemessen brauchte «Das Engineering-Haus.» 636px in
                einer 432px-Spalte: formal einzeilig, praktisch abgeschnitten.

                `clamp()` bindet die Groesse an die Containerbreite (`cqw`) mit
                Unter- und Obergrenze. Der Container ist die Textspalte, nicht
                der Bildschirm — deshalb `@container` am Elternteil.
            */}
            {title && (
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={textShadowStyle}>
                    {renderTitle()}
                </h1>
            )}

            {/* Subtitle */}
            {subtitle && (
                /* `text-balance`: Lead-Text, kein Fliesstext — siehe
                   `docs/rules/coding-style.md` → «Zeilenumbruch». */
                <div className="text-balance text-xl md:text-2xl font-semibold mb-4" style={subtitleShadowStyle}>
                    <RichText content={subtitle} />
                </div>
            )}

            {/* Description */}
            {renderDescription()}

            {/* Trust Badges */}
            {trustBadges && trustBadges.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-6">
                    {trustBadges.map((badgeText, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-sm text-white/90"
                        >
                            <span className="text-green-400 font-bold">✓</span>
                            {badgeText}
                        </span>
                    ))}
                </div>
            )}

            {/* CTA Buttons */}
            {ctaButtons.length > 0 && (
                <div className="flex flex-col sm:flex-row items-start gap-4 mt-6">
                    {ctaButtons.map((button, index) => (
                        <Button
                            key={index}
                            {...button}
                            size="lg"
                            // First button keeps its content variant (default primary);
                            // any further button steps back as secondary.
                            variant={index === 0 ? button.variant ?? "primary" : "secondary"}
                            onDark={true}
                            className="whitespace-nowrap"
                        >
                            {button.text}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );

    // Render image section (reusable across all layouts)
    const renderImage = () => {
        // `visual` schlaegt `image` — siehe Prop-Kommentar.
        if (visual) {
            return <div className="relative w-full max-w-full">{visual}</div>;
        }
        if (!image) {
            return null;
        }

        return (
            <div className="relative w-full max-w-full">
                {/* Custom badge (ReactNode) - positioned absolutely over image */}
                {badge && typeof badge !== "string" && badge}

                <ResponsiveImage
                    src={image}
                    alt={imageAlt}
                    priority={true}
                    sizes="(max-width: 768px) 100vw, (max-width: 1280px) 60vw, 700px"
                    imgClassName={`w-full h-auto ${imageClassName}`}
                    objectFit="contain"
                />
            </div>
        );
    };

    /*
        Zentrierte Darstellung. Genutzt von `layout: "centered"` und vom
        Zweispalter ohne Bild — herausgezogen, damit beide dieselbe Fassung
        erben statt sie zu kopieren.

        `[&_*]:justify-center` faengt die Kinder ein, die von sich aus links
        ausrichten: Der CTA-Block steht auf `items-start`, die Trust-Badges auf
        `flex-wrap` ohne Ausrichtung. Ohne diese Regel klebten Knoepfe und
        Badges am linken Rand, waehrend Titel und Text mittig stuenden — genau
        die halbfertige Zentrierung, die schlimmer aussieht als gar keine.
    */
    const renderCentered = () => (
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto [&_*]:justify-center">
            {renderContent()}
            {image && <div className="mt-8 w-full max-w-2xl">{renderImage()}</div>}
        </div>
    );

    // Render layout based on layout type
    const renderLayout = () => {
        switch (layout) {
            case "two-column":
                /*
                    Ohne Bild ist der Zweispalter kein Zweispalter (Jan, 5.8.2026).

                    Bisher hing nur die Bildspalte an `image`, die Textspalte behielt
                    ihre `md:col-span-5 max-w-xl` — der Text stand also in fuenf von
                    zwoelf Spalten und sieben blieben leer. Sichtbar auf /ueber-uns:
                    Zeilen brachen nach vier Woertern, die rechte Bildschirmhaelfte
                    war leer.

                    Fehlt das Bild, faellt das Layout darum auf die zentrierte
                    Darstellung zurueck — dieselbe, die `layout: "centered"` liefert.
                    Betroffen ist /ueber-uns (DE und EN); alle anderen Zweispalter
                    haben ein Bild und bleiben unveraendert.

                    ⚠️ Geprueft wird `hasImage`, NICHT `image`: Die beiden
                    KI-Workshops reichen ihr Hero-Bild als `children` herein (ein
                    absolut positioniertes Dekorbild). Mit `!image` galten sie als
                    bildlos, ihr Text wurde zentriert und landete auf dem Bild.
                */
                if (!hasImage) {
                    return renderCentered();
                }

                // Text left, image right, space between
                return (
                    <div className="grid grid-cols-1 md:grid-cols-12 items-center">
                        <div className="md:col-span-5 max-w-xl md:pr-8 lg:pr-12">{renderContent()}</div>
                        <div className="md:col-span-7 w-full md:pl-8 lg:pl-12">
                            <div className="w-full flex items-center justify-center md:justify-start">
                                <div className="w-full max-w-full">{renderImage()}</div>
                            </div>
                        </div>
                    </div>
                );

            case "two-column-reverse":
                // Ohne Bild gilt dasselbe wie beim Zweispalter oben: eine halbe
                // Spalte Text neben nichts. Aktuell nutzt keine Seite diese
                // Kombination — die Zeile steht hier, damit die naechste es auch
                // nicht tut. `hasImage` statt `image` aus demselben Grund wie oben.
                if (!hasImage) {
                    return renderCentered();
                }

                // Text right, image left, space between
                return (
                    <div className="grid grid-cols-1 md:grid-cols-12 items-center">
                        <div className="mt-8 md:mt-0 md:col-span-7 md:order-1 w-full md:pr-8 lg:pr-12">
                            <div className="w-full flex items-center justify-center md:justify-start">
                                <div className="w-full max-w-full">{renderImage()}</div>
                            </div>
                        </div>
                        <div className="md:col-span-5 md:order-2 max-w-xl md:pl-8 lg:pl-12">{renderContent()}</div>
                    </div>
                );

            case "centered":
                // Centered layout with text and image centered
                return renderCentered();

            case "single-column":
                // Single column, stacked vertically
                return (
                    <div className={`flex flex-col ${singleColumnClassName}`}>
                        {renderContent()}
                        {image && <div className="mt-8 w-full">{renderImage()}</div>}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <section className={`relative ${bgClass} ${minHeight} overflow-visible`}>
            {/* Custom Children - rendered at section level for absolute positioning */}
            {children}

            <div className={`container mx-auto px-6 md:px-12 lg:px-16 relative z-10 ${className}`}>
                {renderLayout()}

                {/* Discover More Button */}
                {discoverMore && (
                    <div className="flex justify-center mt-12">
                        <Button {...discoverMore} variant="secondary" size="md" onDark={true}>
                            <span className="flex items-center gap-2">
                                {discoverMore.text}
                                <ChevronDown className="w-4 h-4" />
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default HeroSection;
